import { useState, useEffect } from 'react';
import { Table, Input, Row, Col, Badge, Spinner, Card, CardBody, CardHeader, Nav, NavItem, NavLink, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { RefreshCw, Calendar, Calendar as CalendarIcon, UserPlus, FileText, List, XCircle } from 'react-feather';
import { toast } from 'react-toastify';
import { Btn, H5 } from '../../../AbstractElements';
import LegalConsultationService from '../../../Api/services/legal-consultation.service';
import { useAuth } from '../../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import type { components } from '../../../Api/types/openapi';
import LegalConsultationsCalendar from './LegalConsultationsCalendar';
import AssignLawyerModal from './AssignLawyerModal';
import RescheduleConsultationModal from './RescheduleConsultationModal';
import DocumentsModal from './DocumentsModal';

type LegalConsulationResponse = components['schemas']['LegalConsulationResponse'];
type LegalConsulationForClientResponse = components['schemas']['LegalConsulationForClientResponse'];
type LegalConsulationForLawyerResponse = components['schemas']['LegalConsulationForLawyerResponse'];
type PaginationLegalConsulationResponse = components['schemas']['Pagination_LegalConsulationResponse_'];
type PaginationLegalConsulationForClientResponse = components['schemas']['Pagination_LegalConsulationForClientResponse_'];
type PaginationLegalConsulationForLawyerResponse = components['schemas']['Pagination_LegalConsulationForLawyerResponse_'];

type ConsultationData = LegalConsulationResponse | LegalConsulationForClientResponse | LegalConsulationForLawyerResponse;
type PaginationData = PaginationLegalConsulationResponse | PaginationLegalConsulationForClientResponse | PaginationLegalConsulationForLawyerResponse;

const LegalConsultationsList = () => {
  const { hasRole, user } = useAuth();
  const { t } = useTranslation();
  
  // Debug: verificar roles (solo en desarrollo)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && user) {
      console.log('🔍 LegalConsultationsList - Rol del usuario:', {
        roleName: user.role?.name,
        roleNameLower: user.role?.name?.toLowerCase(),
        isAdmin: hasRole('admin'),
        isLawyer: hasRole('lawyer'),
        isClient: hasRole('client'),
        adminOrLawyer: hasRole('admin') || hasRole('lawyer'),
      });
    }
  }, [user, hasRole]);
  const [consultations, setConsultations] = useState<ConsultationData[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  
  // Filtros
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Modales
  const [assignLawyerModalOpen, setAssignLawyerModalOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState<ConsultationData | null>(null);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    loadConsultations();
  }, [currentPage, statusFilter, paymentStatusFilter, dateFilter]);

  const loadConsultations = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit,
      };

      if (statusFilter) params.status = statusFilter;
      if (paymentStatusFilter) params.status_payment = paymentStatusFilter;
      if (dateFilter) params.consultation_date = dateFilter;
      if (searchTerm) params.description = searchTerm;

      let data: PaginationData;
      
      if (hasRole('admin')) {
        data = await LegalConsultationService.getAdminConsultations(params);
      } else if (hasRole('lawyer')) {
        data = await LegalConsultationService.getLawyerConsultations(params);
      } else if (hasRole('client')) {
        data = await LegalConsultationService.getClientConsultations(params);
      } else {
        return;
      }

      // VALIDAR que la respuesta tenga results
      const results = (data as any).results;
      if (!Array.isArray(results)) {
        console.warn('La respuesta no contiene un array de resultados válido');
        setConsultations([]);
        setPagination(data);
        return;
      }
      
      setConsultations(results);
      setPagination(data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al cargar consultas';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'P':
        return <Badge color="warning">Pendiente</Badge>;
      case 'C':
        return <Badge color="danger">Cancelada</Badge>;
      case 'R':
        return <Badge color="info">Reagendada</Badge>;
      case 'S':
        return <Badge color="success">Realizada</Badge>;
      default:
        return <Badge color="secondary">Sin estado</Badge>;
    }
  };

  const getPaymentStatusBadge = (status: string | null) => {
    switch (status) {
      case 'P':
        return <Badge color="warning">Pendiente</Badge>;
      case 'F':
        return <Badge color="danger">Fallido</Badge>;
      case 'S':
        return <Badge color="success">Realizado</Badge>;
      default:
        return <Badge color="secondary">Sin estado</Badge>;
    }
  };

  const handleAssignLawyer = (consultation: ConsultationData) => {
    setSelectedConsultation(consultation);
    setAssignLawyerModalOpen(true);
  };

  const handleReschedule = (consultation: ConsultationData) => {
    setSelectedConsultation(consultation);
    setRescheduleModalOpen(true);
  };

  const handleDocuments = (consultation: ConsultationData) => {
    setSelectedConsultation(consultation);
    setDocumentsModalOpen(true);
  };

  const handleCancel = (consultation: ConsultationData) => {
    setSelectedConsultation(consultation);
    setCancelModalOpen(true);
  };

  const confirmCancel = async () => {
    if (!selectedConsultation?.id) return;

    try {
      setCanceling(true);
      await LegalConsultationService.cancelConsultation(selectedConsultation.id);
      toast.success('Consulta cancelada exitosamente');
      setCancelModalOpen(false);
      setSelectedConsultation(null);
      loadConsultations();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al cancelar la consulta';
      toast.error(errorMessage);
    } finally {
      setCanceling(false);
    }
  };

  const handleModalSuccess = () => {
    loadConsultations();
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
    loadConsultations();
  };

  const renderPagination = () => {
    if (!pagination || (pagination as any).pages <= 1) return null;

    const pages = [];
    const totalPages = (pagination as any).pages;
    
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <Btn
          key={i}
          color={i === currentPage ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setCurrentPage(i)}
          className="me-1"
        >
          {i}
        </Btn>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <span className="text-muted">
            Página {(pagination as any).current_page} de {totalPages} 
            ({(pagination as any).results?.length || 0} resultados)
          </span>
        </div>
        <div>{pages}</div>
      </div>
    );
  };

  if (loading && consultations.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="page-body">
      {/* Tabs de Vista */}
      <Row className="mb-3" style={{ marginTop: '20px' }}>
        <Col md="12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <H5 className="mb-0">Consultas Legales</H5>
            <Nav tabs className="nav-tabs border-tab">
              <NavItem>
                <NavLink
                  className={viewMode === 'list' ? 'active' : ''}
                  onClick={() => setViewMode('list')}
                  style={{ 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    padding: '10px 25px',
                    height: '100%'
                  }}
                >
                  <List size={16} className="me-1" style={{ verticalAlign: 'middle' }} />
                  <span style={{ lineHeight: '1' }}>Lista</span>
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={viewMode === 'calendar' ? 'active' : ''}
                  onClick={() => setViewMode('calendar')}
                  style={{ 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    padding: '10px 25px',
                    height: '100%'
                  }}
                >
                  <Calendar size={16} className="me-1" style={{ verticalAlign: 'middle' }} />
                  <span style={{ lineHeight: '1' }}>Calendario</span>
                </NavLink>
              </NavItem>
            </Nav>
          </div>
        </Col>
      </Row>

      {/* Vista de Calendario */}
      {viewMode === 'calendar' && (
        <Row>
          <Col md="12">
            <LegalConsultationsCalendar />
          </Col>
        </Row>
      )}

      {/* Vista de Lista */}
      {viewMode === 'list' && (
        <>
          {/* Filtros y Búsqueda */}
          <Row className="mb-3">
            <Col md="3">
              <Input
                type="text"
                placeholder={t("Buscar por descripción...")}
                value={searchTerm}
                onChange={handleSearch}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleFilterChange();
                  }
                }}
              />
            </Col>
            <Col md="2">
              <Input
                type="select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  handleFilterChange();
                }}
              >
                <option value="">{t("Todos los estados")}</option>
                <option value="P">{t("Pendiente")}</option>
                <option value="C">{t("Cancelada")}</option>
                <option value="R">{t("Reagendada")}</option>
                <option value="S">{t("Realizada")}</option>
              </Input>
            </Col>
            <Col md="2">
              <Input
                type="select"
                value={paymentStatusFilter}
                onChange={(e) => {
                  setPaymentStatusFilter(e.target.value);
                  handleFilterChange();
                }}
              >
                <option value="">{t("Todos los pagos")}</option>
                <option value="P">{t("Pendiente")}</option>
                <option value="F">{t("Fallido")}</option>
                <option value="S">{t("Realizado")}</option>
              </Input>
            </Col>
            <Col md="2">
              <Input
                type="date"
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  handleFilterChange();
                }}
              />
            </Col>
            <Col md="3" className="text-end">
              <Btn color="secondary" onClick={loadConsultations} className="me-2">
                <RefreshCw size={16} className="me-1" />
                {t("Actualizar")}
              </Btn>
            </Col>
          </Row>

          {/* Tabla de Consultas */}
          <div className="table-responsive theme-scrollbar">
            <Table hover responsive>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Fecha</th>
                      <th>Hora</th>
                      <th>Estado</th>
                      <th>Pago</th>
                      {hasRole('admin') && <th>Cliente</th>}
                      {hasRole(['admin', 'lawyer']) && <th>Abogado</th>}
                      <th>Descripción</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consultations.length === 0 ? (
                      <tr>
                        <td colSpan={hasRole('admin') ? 9 : hasRole(['admin', 'lawyer']) ? 8 : 7} className="text-center">
                          No se encontraron consultas
                        </td>
                      </tr>
                    ) : (
                      consultations.map((consultation) => (
                        <tr key={consultation.id}>
                          <td>#{consultation.id}</td>
                          <td>
                            {consultation.consultation_date 
                              ? new Date(consultation.consultation_date).toLocaleDateString('es-ES')
                              : '-'}
                          </td>
                          <td>
                            {consultation.init_consultation_time && consultation.end_consultation_time
                              ? `${consultation.init_consultation_time} - ${consultation.end_consultation_time}`
                              : '-'}
                          </td>
                          <td>{getStatusBadge(consultation.status)}</td>
                          <td>{getPaymentStatusBadge(consultation.status_payment)}</td>
                          {hasRole('admin') && (
                            <td>
                              {(consultation as any).client?.name || 
                               (consultation as any).client?.email || 
                               'N/A'}
                            </td>
                          )}
                          {hasRole(['admin', 'lawyer']) && (
                            <td>
                              {(consultation as any).lawyer?.name || 
                               (consultation as any).lawyer?.email || 
                               'Sin asignar'}
                            </td>
                          )}
                          <td>
                            <span className="text-truncate d-inline-block" style={{ maxWidth: '200px' }}>
                              {consultation.description || '-'}
                            </span>
                          </td>
                          <td>
                            {/* Reagendar: Admin y Lawyer */}
                            {(hasRole('admin') || hasRole('lawyer')) && (
                              <Btn
                                color="info"
                                size="sm"
                                className="me-1 btn_list_action"
                                onClick={() => handleReschedule(consultation)}
                                title={t("Reagendar")}
                                disabled={consultation.status === 'C' || consultation.status === 'S'}
                              >
                                <CalendarIcon size={14} />
                              </Btn>
                            )}
                            
                            {/* Cancelar: Todos los roles */}
                            <Btn
                              color="danger"
                              size="sm"
                              className="me-1 btn_list_action"
                              onClick={() => handleCancel(consultation)}
                              title={t("Cancelar")}
                              disabled={consultation.status === 'C'}
                            >
                              <XCircle size={14} />
                            </Btn>
                            
                            {/* Asignar Abogado: Solo Admin */}
                            {hasRole('admin') && (
                              <Btn
                                color="primary"
                                size="sm"
                                className="me-1 btn_list_action"
                                onClick={() => handleAssignLawyer(consultation)}
                                title={t("Asignar Abogado")}
                              >
                                <UserPlus size={14} />
                              </Btn>
                            )}
                            
                            {/* Documentos: Todos los roles */}
                            <div className="position-relative d-inline-block">
                              <Btn
                                color="secondary"
                                size="sm"
                                className="btn_list_action"
                                onClick={() => handleDocuments(consultation)}
                                title={t("Documentos")}
                              >
                                <FileText size={14} />
                              </Btn>
                              {(consultation as any).files && (consultation as any).files.length > 0 && (
                                <Badge
                                  color="primary"
                                  pill
                                  className="position-absolute"
                                  style={{
                                    top: '-6px',
                                    right: '-6px',
                                    fontSize: '0.65rem',
                                    padding: '2px 6px',
                                    minWidth: '18px',
                                    lineHeight: '1.3',
                                    fontWeight: '600',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                  }}
                                >
                                  {(consultation as any).files.length}
                                </Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              {renderPagination()}
        </>
      )}

      {/* Modales */}
      {selectedConsultation?.id && (
        <>
          <AssignLawyerModal
            isOpen={assignLawyerModalOpen}
            toggle={() => setAssignLawyerModalOpen(false)}
            consultationId={selectedConsultation.id}
            currentLawyerId={(selectedConsultation as any).lawyer?.id || null}
            consultationDate={selectedConsultation.consultation_date || null}
            consultationStartTime={selectedConsultation.init_consultation_time || null}
            consultationEndTime={selectedConsultation.end_consultation_time || null}
            onSuccess={handleModalSuccess}
          />

          <RescheduleConsultationModal
            isOpen={rescheduleModalOpen}
            toggle={() => setRescheduleModalOpen(false)}
            consultation={selectedConsultation}
            onSuccess={handleModalSuccess}
          />

          <DocumentsModal
            isOpen={documentsModalOpen}
            toggle={() => setDocumentsModalOpen(false)}
            consultationId={selectedConsultation.id}
            consultation={selectedConsultation as LegalConsulationResponse}
            onSuccess={handleModalSuccess}
          />

          {/* Modal de Cancelar */}
          <Modal isOpen={cancelModalOpen} toggle={() => setCancelModalOpen(false)}>
            <ModalHeader toggle={() => setCancelModalOpen(false)}>
              Cancelar Consulta
            </ModalHeader>
            <ModalBody>
              ¿Estás seguro de que deseas cancelar esta consulta? Esta acción no se puede deshacer.
            </ModalBody>
            <ModalFooter>
              <Btn color="secondary" onClick={() => setCancelModalOpen(false)}>
                {t('Cancelar')}
              </Btn>
              <Btn color="primary" onClick={confirmCancel} disabled={canceling}>
                {canceling ? t('Cancelando...') : t('Aceptar')}
              </Btn>
            </ModalFooter>
          </Modal>
        </>
      )}
    </div>
  );
};

export default LegalConsultationsList;

